import { useRef } from "react";
import { RATIO } from "@/lib/noteSnapshot";

const MIN_W = 80;
const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));

// Fixed-ratio selection box over the snapshot. Drag inside to move, drag the
// corner to resize; with a mouse you can also draw a new box anywhere. On touch,
// tapping empty space moves the box there (so the page can still scroll).
export const SelectionBox = ({ box, setBox, bounds }) => {
  const op = useRef(null);
  const fit = (b) => {
    const w = clamp(b.w, MIN_W, Math.min(bounds.w, bounds.h * RATIO));
    const h = w / RATIO;
    return { x: clamp(b.x, 0, bounds.w - w), y: clamp(b.y, 0, bounds.h - h), w, h };
  };
  const start = (kind) => (e) => {
    e.stopPropagation();
    e.currentTarget.setPointerCapture(e.pointerId);
    op.current = { kind, sx: e.clientX, sy: e.clientY, b: box };
  };
  const move = (e) => {
    const o = op.current;
    if (!o) return;
    const dx = e.clientX - o.sx, dy = e.clientY - o.sy;
    if (o.kind === "move") setBox(fit({ ...o.b, x: o.b.x + dx, y: o.b.y + dy }));
    else setBox(fit({ ...o.b, w: o.b.w + Math.max(dx, dy * RATIO) }));
  };
  const end = () => { op.current = null; };
  return (
    <div
      data-testid="preview-select-box"
      onPointerDown={start("move")} onPointerMove={move} onPointerUp={end} onPointerCancel={end}
      className="absolute touch-none cursor-move border-2 border-white rounded-md shadow-[0_0_0_9999px_rgba(0,0,0,0.6)]"
      style={{ left: box.x, top: box.y, width: box.w, height: box.h }}
    >
      <div className="pointer-events-none absolute inset-0 grid grid-cols-3 grid-rows-3">
        {Array.from({ length: 9 }).map((_, i) => <div key={i} className="border border-white/20" />)}
      </div>
      <div
        data-testid="preview-select-resize"
        onPointerDown={start("resize")} onPointerMove={move} onPointerUp={end} onPointerCancel={end}
        className="absolute -right-4 -bottom-4 h-9 w-9 touch-none cursor-nwse-resize flex items-center justify-center"
      >
        <span className="h-4 w-4 rounded-full bg-white shadow ring-2 ring-[#2563EB]" />
      </div>
    </div>
  );
};

export const fitBox = (b, bounds) => {
  const w = clamp(b.w, MIN_W, Math.min(bounds.w, bounds.h * RATIO));
  const h = w / RATIO;
  return { x: clamp(b.x, 0, bounds.w - w), y: clamp(b.y, 0, bounds.h - h), w, h };
};
